import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Outlet, useNavigate } from "react-router-dom";
import PortalNav from "@/components/PortalNav";
import OnboardingBanner from "@/components/OnboardingBanner";
import DemoBanner from "@/components/DemoBanner";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAppContext, RETAILER_IMPERSONATE_KEY, RETAILER_IMPERSONATE_EVENT } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase/client";

export default function PortalLayout() {
  const navigate = useNavigate();
  const { impersonating }                                         = useImpersonation();
  const { needsOnboarding, user, isAdmin, retailerId, isDemo }   = useAppContext();
  const { session }                                               = useAuth();

  /* Lifted dismiss state so PortalLayout can stop rendering the banner
   * immediately on dismiss without waiting for the child to unmount. */
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Retailer impersonation banner
  const [impersonatedStoreName, setImpersonatedStoreName] = useState<string | null>(null);
  const isRetailerImpersonating = isAdmin && !!retailerId;

  useEffect(() => {
    if (!isRetailerImpersonating || !retailerId) { setImpersonatedStoreName(null); return; }
    void supabase
      .from("retailers")
      .select("store_name")
      .eq("id", retailerId)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as { store_name: string } | null;
        setImpersonatedStoreName(row?.store_name ?? retailerId);
      });
  }, [isRetailerImpersonating, retailerId]);

  const showBanner =
    needsOnboarding &&
    !bannerDismissed &&
    !!user?.id &&
    !!session?.access_token;

  return (
    <>
      <DemoBanner />
      {/* Retailer impersonation banner */}
      {isRetailerImpersonating && (
        <div className="fixed top-0 inset-x-0 z-50 h-10 bg-yellow-900/80 border-b border-yellow-700/60 flex items-center justify-between px-6">
          <span className="text-sm text-yellow-200">
            Impersonating <span className="font-semibold">{impersonatedStoreName ?? "…"}</span>
          </span>
          <button
            onClick={() => {
              localStorage.removeItem(RETAILER_IMPERSONATE_KEY);
              window.dispatchEvent(new Event(RETAILER_IMPERSONATE_EVENT));
              navigate("/admin/retailers");
            }}
            className="text-xs rounded border border-yellow-600 px-3 py-1 text-yellow-300 hover:bg-yellow-800/40 transition"
          >
            Exit
          </button>
        </div>
      )}
      {/* 🌟 Portal-specific metadata */}
      <Helmet>
        <title>Fyrris Portal – Design your moment</title>
        <meta
          name="description"
          content="Access your Fyrris retailer portal to manage locations, assets, billing, analytics, and AR experiences. Design your moment with Fyrris."
        />
        <meta property="og:title" content="Fyrris Portal – Design your moment" />
        <meta
          property="og:description"
          content="Manage your Fyrris retailer account, products, and analytics in one simple dashboard."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fyrris.com/portal" />
      </Helmet>

      {/* 🔧 Portal Navigation + Content */}
      <PortalNav />
      {/* pt-14 = nav height; +40px (top-10) when a banner above the nav is visible */}
      <main className={`min-h-screen bg-gray-950 text-gray-100 ${(impersonating || isDemo) ? "pt-[96px]" : "pt-14"}`}>
        {showBanner && (
          <OnboardingBanner
            userId={user.id}
            jwt={session.access_token}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
        <Outlet />
      </main>
    </>
  );
}
